import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SARAH_IMG = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&h=200&fit=crop&crop=face";

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Sarah, your AI financial advisor at AffordScope. Ask me anything about cost of living, salaries, or budgeting across US cities. (Sarah is an AI assistant — not a licensed financial advisor.)" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { role: "assistant", text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "Sorry, I'm having trouble connecting. Please try again." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chatbot-toggle"
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 bg-primary text-white px-4 py-3 rounded-full shadow-lg hover:bg-primary/90 transition-all hover:scale-105">
          <img src={SARAH_IMG} alt="Sarah" className="w-8 h-8 rounded-full object-cover border-2 border-white/30" />
          <span className="text-sm font-medium hidden sm:inline">Chat with Sarah</span>
          <MessageCircle className="w-5 h-5 sm:hidden" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-20 right-4 z-40 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col" 
          style={{ height: '500px' }} data-testid="chatbot-window">
          <div className="flex items-center gap-3 p-4 border-b border-slate-100 bg-primary rounded-t-2xl">
            <img src={SARAH_IMG} alt="Sarah" className="w-10 h-10 rounded-full object-cover border-2 border-white/30" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-white">Sarah</p>
              <p className="text-xs text-emerald-200">AI Financial Advisor</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="chatbot-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === 'user' 
                    ? 'bg-primary text-white rounded-br-md' 
                    : 'bg-slate-100 text-slate-700 rounded-bl-md'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-md">
                  <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-slate-100">
            <p className="text-[10px] text-slate-400 mb-2 text-center">Sarah is an AI assistant. Not financial advice.</p>
            <div className="flex gap-2">
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Ask about cost of living..."
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="chatbot-input" />
              <Button size="sm" onClick={send} disabled={loading || !input.trim()} className="bg-primary hover:bg-primary/90" data-testid="chatbot-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
